import { useDispatch, useSelector } from "react-redux"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { ServiceCategory } from "src/common/models/serviceCategory"
import { PageCard } from "src/common/styles/styles"
import { SearchableTable } from "src/components/SearchableTable"
import useUserPermission from "src/hooks/useUserPermissions"
import { RootState } from "src/state/reducer"
import { GET_SERVICE_CATEGORIES_API } from "../state/actionTypes"
import { ColumnsKeys, TABLE_COLUMNS } from "./config"
import { Button, Space, Tooltip, notification } from "antd"
import { EditOutlined, ProfileOutlined } from '@ant-design/icons'
import { SystemDescriptions } from "src/common/descriptions"
import { searchableTableUtils } from "src/components/SearchableTable/utils"
import { ColumnsType } from "antd/es/table"
import { getServiceCategoriesApi } from "../state/action"
import { useEffect, useState } from "react"
import { CreateServiceCategory } from "./CreateServiceCategory"
import { USER_SCOPES } from "src/hooks/UserPermission"

export type ReduxProps = {
    serviceCategories?: ServiceCategory[],
    isGettingCategories: boolean,
}

export const ServiceCategories = () => {
    const dispatch = useDispatch()
    const { validateUserScopeForPage } = useUserPermission()

    const { isUserAllowedToPerformAdminActions: userAdminActions } = useUserPermission()

    const [modalCreateOpen, setModalCreateOpen] = useState<boolean>(false)
    const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | undefined>(undefined)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        serviceCategories: state.service.serviceCategories,
        isGettingCategories: serviceLoading(state, [GET_SERVICE_CATEGORIES_API]),
    }))

    useEffect(() => {
        validateUserScopeForPage(USER_SCOPES.SERVICE_CATEGORY_READ)
        dispatch(getServiceCategoriesApi())
    }, [])

    const descriptions = SystemDescriptions.PAGES.SERVICE.SERVICE_CATEGORIES
    const createDescriptions = SystemDescriptions.PAGES.SERVICE.CREATE
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const tblColumns: ColumnsType<ServiceCategory> = [
        ...TABLE_COLUMNS,
        {
            title: descriptions.TABLE_COLUMNS.ACTIONS,
            dataIndex: ColumnsKeys.ACTIONS,
            key: ColumnsKeys.ACTIONS,
            align: searchableTableUtils.alignment.centerAlign,
            render: (_, record: ServiceCategory) => {
                const handleEditClick = (category: ServiceCategory) => {
                    setSelectedCategory(category)
                    setModalCreateOpen(true)
                }

                const handleViewClick = (customerId: number) => {
                    // navigate(Urls.FRONTEND.CUSTOMER.CUSTOMER_PROFILE_PAGE(customerId))
                }

                return <Space size="middle">
                    <Tooltip title={commonDescriptions.ACTIONS.VIEW} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<ProfileOutlined />}
                            onClick={() => handleViewClick(record.id!)}
                        />
                    </Tooltip>
                    {
                        userAdminActions
                            ?
                            <Tooltip title={commonDescriptions.ACTIONS.EDIT} >
                                <Button type="primary" ghost shape="circle" size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditClick(record)}
                                />
                            </Tooltip>
                            : null
                    }
                </Space>
            },
        },
    ]

    const handleActionClick = () => {
        setSelectedCategory(undefined)
        setModalCreateOpen(true)
    }

    const openNotification = (message: string, description: string) => {
        notification.success({
            message,
            description,
        });
    };

    const onFinish = () => {
        openNotification(
            createDescriptions.SUCCESS_MODAL.TITLE,
            createDescriptions.SUCCESS_MODAL.DESCRIPTION
        )
        setModalCreateOpen(false)
        setSelectedCategory(undefined)
        dispatch(getServiceCategoriesApi())
    }

    return (
        <>
            <PageCard
                size="small"
                title={descriptions.TITLE}
            >
                <SearchableTable
                    showSearch
                    showActionButton={userAdminActions}
                    actionButtonLabel={descriptions.TABLE.ACTION_BUTTON}
                    actionButtonOnClick={handleActionClick}
                    showLoader={reduxProps.isGettingCategories}
                    items={reduxProps.serviceCategories || []}
                    tableColumns={tblColumns}
                    searchLabel={descriptions.TABLE.SEARCH_PLACEHOLDER}
                />
            </PageCard>
            <CreateServiceCategory
                open={modalCreateOpen}
                initialValues={selectedCategory}
                onFinish={onFinish}
                onCancel={function (): void {
                    setModalCreateOpen(false)
                    setSelectedCategory(undefined)
                }}
            />
        </>
    )
}
